import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import authService from '../../../lib/authService';
import LeftPanel from './LeftPanel';
import { pushCleverTapEvent } from '../pushCleverTapEvent';

const schema = yup.object({
  email: yup.string().email('Invalid email format').required('Email is required'),
  mobileNumber: yup.string()
    .required('Mobile number is required')
    .matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits')
});

const SignUp = ({ onNext, onLoginClick }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues
  } = useForm({
    resolver: yupResolver(schema)
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await authService.sendOTP({
        email: data.email,
        mobileNumber: parseInt(data.mobileNumber),
        countryCode: '+91',
        countryName: 'India'
      });
      
      // Check if the API call was successful
      if (response.success && response.result) {
        window.gtag('event', 'get_otp');
        pushCleverTapEvent("get_otp",{'PageName': 'Sign_up', email: data.email, number: data.mobileNumber});
        // Pass the form data and response to next step
        onNext({
          formData: data,
          requestId: response.result.requestId,
          response
        });
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-[20px] shadow-xl w-full max-w-3xl flex overflow-hidden">
        <LeftPanel />

        {/* Right Panel */}
        <div className="w-[55%] p-8 relative sm:w-full sm:p-3">
          <button 
            onClick={() => onLoginClick && onLoginClick()}
            className="absolute top-4 right-4 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-bold text-black">Sign Up</h3>
            <p className="text-[#979797] mb-4 text-xs w-4/5">Join thousands of businesses financing their supply chain with a few simple steps.</p>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='mt-2'>
                <label className="block text-sm font-semibold text-[#3C3C3C] mb-1">
                  Email ID*
                </label>
                <input
                  type="email"
                  {...register('email')}
                  placeholder="Enter your email ID"
                  className="w-full p-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              <div className='mt-2'>
                <label className="block text-sm font-semibold text-[#3C3C3C] mb-1">
                  Mobile Number*
                </label>
                <div className="flex">
                  <div className="flex items-center px-1 py-1 text-sm bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg">
                    <span className="text-gray-700">+91</span>
                  </div>
                  <input
                    type="tel"
                    {...register('mobileNumber')}
                    placeholder="Enter your mobile number"
                    maxLength={10}
                    className="flex-1 p-1 border border-gray-300 text-sm rounded-r-lg focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                  />
                </div>
                {errors.mobileNumber && (
                  <p className="text-red-500 text-sm mt-1">{errors.mobileNumber.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand text-white px-1 py-2 sm:py-1 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all mt-5 text-sm"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending OTP...
                  </div>
                ) : (
                  'Get OTP'
                )}
              </button>
            </form>

            {error && (
              <div className="text-red-700 rounded-lg my-1 text-sm">
                {error}
              </div>
            )}

            <div className="mt-3 sm:mt-1 text-center">
              <span className="text-[#979797] text-sm">Already have an account? </span>
              <button
                onClick={onLoginClick}
                className="text-brand font-medium hover:underline"
              >
                Login Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;